<template>
  <form
    v-if="DEBUG_RFP_FORM_DATA"
    v-on:submit.prevent="debugReplace"
  >
    <DbTextarea
      v-model="debugVersionData"
      class="db-tw-mb-4"
    >
      {{ $t('Debug form data is only visible for developpers') }}
    </DbTextarea>
    <button
      type="submit"
      class="db-tw-mb-10 db-tw-mt-10 md:db-tw-mt-4 db-button-large db-button-primary"
    >
      {{ $t('replace data') }}
    </button>
  </form>

  <RfpLayout>
    <template
      v-if="steps.length > 1"
      v-slot:stepper
    >
      <DbRfpStepper
        :steps="steps"
        v-on:change-step="navStep"
      />
    </template>
    <template v-slot:form>
      <RfpFormBuilder
        v-model="formData"
        :step-number="currentStep"
        :schema="stepsSchema[currentStep].rfp_fields"
        :buttonText="confirmationText"
        v-on:update:validation="onValidation"
        v-on:update:submit="onSubmit"
      />
    </template>
  </RfpLayout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Steps } from '@/features/RfpForm/types/Steps'
import { useScrollToElement, useDebugUpdateData } from '@/features/RfpForm/composables/useRfpForm'
import type { RfpFormStep } from '@/features/RfpForm/types/RfpFormSteps'
import type { RfpFormData } from '@/features/RfpForm/types/RfpFormData'
import { DEBUG_RFP_FORM_DATA } from '@/config'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  stepsSchema: RfpFormStep[]
  defaultData: RfpFormData
}>()

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'submit-form', formData: RfpFormData): void
}>()

/* Variables Form */
const currentStep = ref<number>(0)
const steps = ref<Steps[]>([])
const buttonTextContinue = t('Continue')
const buttonTextSend = t('Send Request')

/**
 * @function computed - confirmationText
 * Change button submit text based on current step
 */
const confirmationText = computed(() =>
  currentStep.value < props.stepsSchema.length - 1 ? buttonTextContinue : buttonTextSend,
)

/**
 * Better structure for steps array
 * props passed to DbRfpStepper
 */
props.stepsSchema.map((step, index: number) => {
  steps.value.push({
    label: step.name,
    current: index === currentStep.value ? true : false,
    completed: index < currentStep.value && index !== 0 ? true : false,
  })
})

/*
  Creation of the formdata object
  defaultData is only here as the initial value,
  updates are not reflected after
*/
let formData = ref<RfpFormData>({
  ...props.defaultData,
})

/**
 * Get data from the debug textarea and set it nicely for form data
 *
 */
const { debugReplace, debugVersionData, debugWatcher } = useDebugUpdateData(formData.value)
if (DEBUG_RFP_FORM_DATA) {
  debugWatcher()
}

/**
 * @function navStep - on click on stepper links
 * @param step: number
 * Update currentStep value based on stepper click and update stepper display
 */
function navStep(step: number) {
  currentStep.value = step
  changeCurrentStateStep()
}

/**
 * @function changeCurrentStateStep
 * Update only the current state in steps array (data for the stepper)
 */
function changeCurrentStateStep() {
  /* reset current on steps array */
  for (const step of steps.value) {
    step.current = false
  }
  /* update steps array with current step */
  steps.value[currentStep.value].current = true
}

/**
 * @function onSubmit - on click on submit button
 * if value => Go to the next step or send form
 */
function onSubmit() {
  if (currentStep.value < steps.value.length - 1) {
    goToNextStep()
  } else {
    if (DEBUG_RFP_FORM_DATA) {
      console.debug(formData.value)
    }
    emit('submit-form', formData.value)
  }
}

/**
 * @function goToNextStep
 */
function goToNextStep() {
  useScrollToElement('.db-rfp-container')
  currentStep.value++

  /* update stepper */
  changeCurrentStateStep() // update current
  // update completed
  const indexCompleted = currentStep.value === 0 ? 0 : currentStep.value - 1
  steps.value[indexCompleted].completed = true // change steps completed when on next step
}

/**
 * @function onValidation - on change isFormValid
 * @param formIsInvalid: boolean
 * Update stepper if form is invalid
 */
function onValidation(formIsInvalid: boolean) {
  if (formIsInvalid) {
    changeCurrentStateStep()
    /* Update stepper completed state */
    for (let j = steps.value.length - 1; j >= currentStep.value; j--) {
      steps.value[j].completed = false
    }
  }
}
</script>
